import { z } from 'zod'

import type { StockMovementPayload } from './inventory-command'
import { inventoryAllocationFields } from '../types/inventory-allocation'

export interface InventoryAllocationDraft {
  serials: { id: string; label: string }[]
  batches: {
    id: string
    label: string
    quantity: string
    availableQuantity: number
  }[]
  newSerialText: string
  newBatches: {
    batchNumber: string
    quantity: string
    expirationDate: string
  }[]
}
export const emptyInventoryAllocationDraft = (): InventoryAllocationDraft => ({
  serials: [],
  batches: [],
  newSerialText: '',
  newBatches: [],
})
const fields = z.object(inventoryAllocationFields).strict()
export function buildInventoryAllocations(
  draft: InventoryAllocationDraft,
  item: { isSerialized?: boolean; hasBatches?: boolean },
  receiving: boolean,
  quantity: number,
): Pick<
  StockMovementPayload,
  'serialIds' | 'batchAllocations' | 'newSerialNumbers' | 'newBatches'
> {
  const newSerialNumbers = draft.newSerialText
    .split(/\r?\n/)
    .map(value => value.trim())
    .filter(Boolean)
  const parsed = fields.safeParse({
    ...(item.isSerialized
      ? {
          serialIds: draft.serials.map(serial => serial.id),
          ...(newSerialNumbers.length ? { newSerialNumbers } : {}),
        }
      : {}),
    ...(item.hasBatches
      ? {
          batchAllocations: draft.batches.map(batch => ({
            batchId: batch.id,
            quantity: /^\d+$/.test(batch.quantity.trim())
              ? Number(batch.quantity)
              : NaN,
          })),
          ...(draft.newBatches.length
            ? {
                newBatches: draft.newBatches.map(batch => ({
                  batchNumber: batch.batchNumber,
                  quantity: /^\d+$/.test(batch.quantity.trim())
                    ? Number(batch.quantity)
                    : NaN,
                  ...(batch.expirationDate
                    ? { expirationDate: batch.expirationDate }
                    : {}),
                })),
              }
            : {}),
        }
      : {}),
  })
  if (!parsed.success)
    throw new Error(
      parsed.error.issues[0]?.message ?? 'Check the allocation details',
    )
  const payload = parsed.data
  if (
    !receiving &&
    (payload.newSerialNumbers?.length || payload.newBatches?.length)
  )
    throw new Error('New identifiers can only be added when receiving stock')
  if (
    item.isSerialized &&
    (payload.serialIds?.length ?? 0) +
      (payload.newSerialNumbers?.length ?? 0) !==
      Math.abs(quantity)
  )
    throw new Error('Select or enter one serial number per unit moved')
  if (
    item.hasBatches &&
    [...(payload.batchAllocations ?? []), ...(payload.newBatches ?? [])].reduce(
      (sum, row) => sum + row.quantity,
      0,
    ) !== Math.abs(quantity)
  )
    throw new Error('Batch quantities must add up to the movement quantity')
  if (
    !receiving &&
    draft.batches.some(
      batch => Number(batch.quantity) > batch.availableQuantity,
    )
  )
    throw new Error(
      'A selected batch has insufficient stock at this source location. Refresh availability or reduce its quantity.',
    )
  return payload
}
